import React, { useEffect, useCallback, useRef, useState } from 'react';
import { Button, Icon } from '@iq/react-components';
import { v4 as uuidv4 } from 'uuid';
import { capitalize } from '../../../../../utils';

import ColumnItem from '../ColumnItem';
import ItemHandler from '../ItemHandler';
import Delete from '../../../../Delete';
import JsonEditor from '../../../../JsonEditor';
import Heading from '../../../../Heading';
import TypeLabel from '../../../../TypeLabel';
import StyledItem from '../../../../StyledItem';

import sectionSchema from '../../schemas/sectionSchema';
import { displaySchema, displayUiSchema } from '../../schemas/displaySchema';
import { canAddColumn, canChangeSectionHeight } from '../../utils/sectionValidation';

// To remove duplicate error message
const transformErrors = (errors) =>
  errors.map((error) => {
    switch (error.name) {
      case 'required': {
        if (error.property === 'title') {
          error.message = '';
        }
        break;
      }
      default:
    }
    return error;
  });

const SectionItem = ({
  activeTree = {},
  sectionIndex,
  sectionCount,
  moveSection,
  removeSection,
  updateSection,
  updateColumn,
  sectionActive,
  setActiveTreeIndexes,
  title,
  id,
  displayTitle,
  displayWhen,
  height,
  columns = [],
}) => {
  const sectionReference = useRef(null);
  const [columnError, setColumnError] = useState(null);

  const { page: activePageIndex = null, column: activeColumnIndex = null } = activeTree;

  useEffect(() => {
    if (sectionActive && sectionReference.current && activeColumnIndex === null) {
      sectionReference.current.scrollIntoView();
    }
  }, [sectionActive, activeColumnIndex, sectionReference.current]);

  const createColumn = (event) => {
    event?.stopPropagation();
    // Validate if column can be added
    const validation = canAddColumn(columns);

    if (!validation.allowed) {
      setColumnError(validation.reason);
      return;
    }

    setColumnError(null);
    setActiveTreeIndexes({
      ...activeTree,
      column: columns.length,
      panel: undefined,
      initial: false,
    });
    updateSection(activePageIndex, sectionIndex, (section) => ({
      ...section,
      columns: [
        ...columns,
        {
          id: `column-${uuidv4()}`,
          title: 'Untitled column',
          panels: [],
        },
      ],
    }));
  };

  const moveColumn = (from, to) => {
    updateSection(activePageIndex, sectionIndex, (section) => {
      const column = section.columns[from];
      const updatedColumns = [...section.columns];
      updatedColumns.splice(from, 1);
      updatedColumns.splice(to, 0, column);
      return {
        ...section,
        columns: updatedColumns,
      };
    });
    setActiveTreeIndexes({
      ...activeTree,
      column: to,
      initial: false,
    });
  };

  const removeColumn = (columnIndex) => {
    setActiveTreeIndexes({
      ...activeTree,
      column: undefined,
      panel: undefined,
      initial: false,
    });
    updateSection(activePageIndex, sectionIndex, (section) => {
      const updatedColumns = [...section.columns];
      updatedColumns.splice(columnIndex, 1);
      return {
        ...section,
        columns: updatedColumns,
      };
    });
  };

  const updateSectionProperties = useCallback(
    ({ formData: sectionProperties }) => {
      updateSection(activePageIndex, sectionIndex, (section) => ({
        ...section,
        ...sectionProperties,
      }));
    },
    [updateSection, activePageIndex, sectionIndex]
  );

  const updateDisplayProperties = useCallback(
    ({ formData: newDisplaySettings }) => {
      updateSection(activePageIndex, sectionIndex, (page) => ({
        ...page,
        displayWhen: newDisplaySettings,
      }));
    },
    [updateSection, activePageIndex, sectionIndex]
  );

  const toggleSection = () => {
    if (sectionActive) {
      setActiveTreeIndexes({
        ...activeTree,
        section: undefined,
        column: undefined,
        panel: undefined,
        initial: false,
      });
    } else {
      setActiveTreeIndexes({
        ...activeTree,
        section: sectionIndex,
      });
    }
  };

  // If we upgrade to RJSFv5, we can try to get rid of the custom validation.
  const customValidate = useCallback(
    (formData, errors) => {
      for (const fieldName of sectionSchema.required) {
        const validValue = formData[fieldName];
        if (!validValue) {
          errors[fieldName].addError(`Section ${capitalize(fieldName)} is required`);
        }
      }

      // Validate section height change
      if (formData.height !== height) {
        const validation = canChangeSectionHeight(formData.height, columns);
        if (!validation.allowed) {
          errors.height.addError(validation.reason);
        }
      }

      return errors;
    },
    [sectionSchema, height, columns]
  );

  return (
    <StyledItem
      ref={sectionReference}
      itemClass="section-list-item"
      active={sectionActive}
      onClick={toggleSection}
      headerLabel={<TypeLabel type="section" />}
      headerContent={
        <div className="title">
          {title}
          {height && height !== 'fit-content' && (
            <span style={{ marginLeft: '0.5rem', fontSize: '0.9em', opacity: 0.7 }}>
              (
              {
                sectionSchema.properties.height.enumNames[
                  sectionSchema.properties.height.enum.indexOf(height)
                ]
              }
              )
            </span>
          )}
        </div>
      }
      headerActions={
        <>
          <ItemHandler
            move={moveSection}
            index={sectionIndex}
            count={sectionCount}
          />
          <Icon icon={sectionActive ? 'he-up' : 'he-down'} />
        </>
      }
    >
      <JsonEditor
        title="Section properties"
        formData={{ title, id, displayTitle, height: height ?? 'fit-content' }}
        schema={sectionSchema}
        onFormSubmit={updateSectionProperties}
        saveButtonText={'Update section properties'}
        customValidate={customValidate}
        customTransformErrors={(errors) => transformErrors(errors)}
      />

      <JsonEditor
        title="Display properties"
        formData={displayWhen}
        uiSchema={displayUiSchema}
        schema={displaySchema}
        onFormSubmit={updateDisplayProperties}
        saveButtonText={'Update display properties'}
      />

      <Heading
        contentLeft={
          <>
            <div className="title">Columns</div>
            <Button
              onClick={createColumn}
              activity="secondary"
              slim
            >
              Create column
            </Button>
            {columnError && <span className="column-error">{columnError}</span>}
          </>
        }
      />

      {columns.map((column, index) => (
        <ColumnItem
          key={index}
          activeTree={activeTree}
          columnCount={columns.length}
          moveColumn={moveColumn}
          removeColumn={removeColumn}
          pageIndex={activePageIndex}
          sectionIndex={sectionIndex}
          columnIndex={index}
          updateColumn={updateColumn}
          columnActive={sectionActive && activeColumnIndex === index}
          setActiveTreeIndexes={setActiveTreeIndexes}
          sectionHeight={height}
          {...column}
        />
      ))}

      <Delete
        onDelete={() => removeSection(sectionIndex)}
        title="Delete section"
      />
    </StyledItem>
  );
};

export default SectionItem;
